
import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Download, RefreshCw, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { fetchAnalytics } from '../services/n8nService';
import {
  type CompletedCallRow,
  type QueueRow,
  completedCallStatusLabel,
  completedCallResultLine,
  completedTimestamp,
} from '../services/analyticsMappers';

type QueueItem = QueueRow;

interface HistoryEntry {
  id: string;
  name: string;
  phone: string;
  status: 'Completed' | 'In-Queue' | 'Failed' | 'Processing' | 'Requeued' | 'Dispatched';
  timestamp: string;
  rawTimestamp: string;
  outcome: string;
  duration: string;
  attempts: number;
  callId: string | null;
  type: 'queue' | 'completed';
  callOutcome?: string | null;
  sentiment?: string | null;
  meetingStatus?: string | null;
  nextAction?: string | null;
  failureReason?: string | null;
}

// Helper to format date
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  }) + ' · ' + date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

// Helper to format duration
const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const History: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [historyData, setHistoryData] = useState<HistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const loadHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rawData = await fetchAnalytics();
      const response = Array.isArray(rawData) ? rawData : [rawData];
      
      const entries: HistoryEntry[] = [];
      
      // Process analytics response
      for (const item of response) {
        // Process queue items
        const queueData = Array.isArray(item.activeQueue)
          ? item.activeQueue
          : item.activeQueue?.data || [];
        if (queueData.length > 0) {
          for (const queueItem of queueData as QueueItem[]) {
            const qs = queueItem.status?.toLowerCase();
            const queueStatus: HistoryEntry['status'] =
              qs === 'pending' ? 'In-Queue' :
              qs === 'processing' ? 'Processing' :
              qs === 'dispatched' ? 'Dispatched' : 'In-Queue';
            entries.push({
              id: `Q-${queueItem.id}`,
              name: queueItem.name || 'Unknown Contact',
              phone: queueItem.number || '-',
              status: queueStatus,
              timestamp: formatDate((queueItem as QueueRow).created_at || queueItem.createdAt || ''),
              rawTimestamp: (queueItem as QueueRow).created_at || queueItem.createdAt || '',
              outcome: queueItem.attempts > 0 ? `Attempt ${queueItem.attempts}` : 'Waiting',
              duration: '-',
              attempts: queueItem.attempts,
              callId: null,
              type: 'queue'
            });
          }
        }
        
        // Process completed calls
        const completedData = Array.isArray(item.completedCalls)
          ? item.completedCalls
          : item.completedCalls?.data || [];
        if (completedData.length > 0) {
          for (const call of completedData as CompletedCallRow[]) {
            // Skip empty/placeholder records
            if (!call.call_id && !call.name && !call.phone_number) continue;

            const label = completedCallStatusLabel(call);
            const status: HistoryEntry['status'] =
              label === 'Failed' ? 'Failed' :
              label === 'Requeued' ? 'Requeued' :
              label === 'Completed' ? 'Completed' : 'Completed';

            entries.push({
              id: call.call_id || `C-${call.id}`,
              name: call.name || 'Unknown Contact',
              phone: call.phone_number || '-',
              status,
              timestamp: formatDate(completedTimestamp(call) || call.created_at || call.createdAt || ''),
              rawTimestamp: completedTimestamp(call) || call.created_at || call.createdAt || '',
              outcome: completedCallResultLine(call),
              duration: formatDuration(call.total_duration_seconds),
              attempts: 1,
              callId: call.call_id,
              type: 'completed',
              callOutcome: call.call_outcome ?? null,
              sentiment: call.user_sentiment ?? null,
              meetingStatus: call.meeting_status ?? null,
              nextAction: call.next_action ?? null,
              failureReason: call.failure_reason ?? null,
            });
          }
        }
      }
      
      // Sort by timestamp (newest first)
      entries.sort((a, b) => new Date(b.rawTimestamp).getTime() - new Date(a.rawTimestamp).getTime());
      
      setHistoryData(entries);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('Failed to load call history from n8n');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const filteredHistory = useMemo(() => {
    let results = historyData;
    
    // Filter by status
    if (filterStatus !== 'all') {
      results = results.filter(entry => entry.status === filterStatus);
    }
    
    // Filter by search term
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      results = results.filter(entry => 
        entry.name.toLowerCase().includes(term) || 
        entry.phone.includes(term) ||
        entry.id.toLowerCase().includes(term) ||
        entry.outcome.toLowerCase().includes(term) ||
        (entry.callOutcome && entry.callOutcome.toLowerCase().includes(term)) ||
        (entry.nextAction && entry.nextAction.toLowerCase().includes(term))
      );
    }
    
    return results;
  }, [searchTerm, historyData, filterStatus]);

  const exportToCSV = () => {
    const headers = ['ID', 'Name', 'Phone', 'Status', 'Outcome', 'Duration', 'Timestamp'];
    const rows = filteredHistory.map(entry => [
      entry.id,
      entry.name,
      entry.phone,
      entry.status,
      entry.outcome,
      entry.duration,
      entry.timestamp
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusCounts = useMemo(() => ({
    all: historyData.length,
    'In-Queue': historyData.filter(e => e.status === 'In-Queue' || e.status === 'Processing' || e.status === 'Dispatched').length,
    'Completed': historyData.filter(e => e.status === 'Completed').length,
    'Failed': historyData.filter(e => e.status === 'Failed').length,
    'Requeued': historyData.filter(e => e.status === 'Requeued').length,
  }), [historyData]);

  return (
    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden animate-in fade-in duration-500 transition-colors">
      <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Call Dispatch Log</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Audit trail for all automated AI agent communications.</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={loadHistory}
            disabled={isLoading}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-xl transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search by name or phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 w-64 transition-all dark:text-slate-100"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 text-sm bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-slate-100"
          >
            <option value="all">All ({statusCounts.all})</option>
            <option value="In-Queue">In Queue ({statusCounts['In-Queue']})</option>
            <option value="Completed">Completed ({statusCounts['Completed']})</option>
            <option value="Requeued">Requeued ({statusCounts['Requeued']})</option>
            <option value="Failed">Failed ({statusCounts['Failed']})</option>
          </select>
          <button 
            onClick={exportToCSV}
            disabled={filteredHistory.length === 0}
            className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-sm hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 dark:bg-rose-950/20 border-b border-rose-100 dark:border-rose-900 text-rose-600 dark:text-rose-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={loadHistory} className="font-bold hover:underline">Retry</button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest border-b dark:border-slate-800">
            <tr>
              <th className="px-6 py-4">ID</th>
              <th className="px-6 py-4">Recipient</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Outcome</th>
              <th className="px-6 py-4">Duration</th>
              <th className="px-6 py-4">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-8 py-12 text-center text-slate-400 dark:text-slate-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Loader2 className="w-8 h-8 animate-spin opacity-50" />
                    <p className="font-medium">Loading call history...</p>
                  </div>
                </td>
              </tr>
            ) : filteredHistory.length > 0 ? (
              filteredHistory.map((entry) => (
                <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4 text-xs text-slate-400 dark:text-slate-500 font-mono">{entry.id}</td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-slate-800 dark:text-slate-100">{entry.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{entry.phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      entry.status === 'Completed' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' : 
                      entry.status === 'Failed' ? 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400' : 
                      entry.status === 'Processing' ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400' :
                      entry.status === 'Requeued' ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300' :
                      entry.status === 'Dispatched' ? 'bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300' :
                      'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400'
                    }`}>
                      {entry.status === 'Completed' && <CheckCircle2 className="w-3 h-3" />}
                      {entry.status === 'Failed' && <XCircle className="w-3 h-3" />}
                      {(entry.status === 'In-Queue' || entry.status === 'Processing' || entry.status === 'Dispatched') && <Clock className="w-3 h-3" />}
                      {entry.status === 'Requeued' && <Clock className="w-3 h-3" />}
                      <span>{entry.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 max-w-md">
                    <div className="leading-snug">{entry.outcome}</div>
                    {(entry.callOutcome || entry.sentiment || entry.meetingStatus || entry.nextAction || entry.failureReason) && (
                      <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 font-mono break-words">
                        {[entry.callOutcome, entry.sentiment, entry.meetingStatus, entry.nextAction, entry.failureReason]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 font-mono">{entry.duration}</td>
                  <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">{entry.timestamp}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-8 py-12 text-center text-slate-400 dark:text-slate-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Search className="w-8 h-8 opacity-20" />
                    {searchTerm ? (
                      <>
                        <p className="font-medium">No results matching "{searchTerm}"</p>
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="text-indigo-600 dark:text-indigo-400 text-xs font-bold hover:underline"
                        >
                          Clear search
                        </button>
                      </>
                    ) : (
                      <p className="font-medium">No call history available</p>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {filteredHistory.length > 0 && (
        <div className="p-4 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
          <span>Showing {filteredHistory.length} of {historyData.length} Entries</span>
          <div className="flex items-center space-x-4">
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span>Queue: {statusCounts['In-Queue']}</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span>Completed: {statusCounts['Completed']}</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              <span>Requeued: {statusCounts['Requeued']}</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              <span>Failed: {statusCounts['Failed']}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
